import {FloatingLabel, Form} from "react-bootstrap";
import styled from "styled-components";
import {useState} from "react";
import Icon from "./Icon";
import parse from "html-react-parser";
import Badge from "./Badge";
import {getTheme} from "../shared/themeUtils";
import CustomTooltip from "./CustomTooltip";

const Contain = styled.div`
    margin-top: ${getTheme("space.2")};
    text-align: left;
    position: relative;
    width: 100%;

    .optional {
        position: absolute;
        right: 10px;
        top: -12px;

        span {
            font-size: 12px;
        }
    }

    label {
        font-size: ${getTheme("fontSizes.small")};
        color: ${getTheme("colors.secondaryFont")};
    }

    .show-password {
        cursor: pointer;
        display: inline-block;
        position: absolute;
        top: ${getTheme("space.2")};
        right: ${getTheme("space.2")};
        padding: ${getTheme("space.0")};

        div {
            display: flex;
            align-items: center;
        }
    }
`

export enum InputTypeEnum {
  TEXT = "text",
  EMAIL = "email",
  PASSWORD = "password",
  TEXTAREA = "textarea",
}

type CustomInputPropsType = {
  className?: string,
  type: InputTypeEnum,
  label: string,
  id?: string,
  placeholder: string,
  icon?: string,
  value: string,
  setValue: any,
  errorMsg?: string[],
  required?: boolean,
  isInvalid?: boolean,
  optional?: boolean,
  optionalText?: string,
  tooltipTitle?: string,
  tooltipContent?: string,
  disabled?: boolean,
  maxLength?: number,
  minLength?: number,
}

const CustomInput = (props: CustomInputPropsType) => {
  const {
    className, type, id, label, placeholder, disabled, value, setValue, required, errorMsg, isInvalid, optional,
    maxLength, minLength, optionalText, tooltipTitle, tooltipContent
  } = props;
  const [showPassword, setShowPassword] = useState(false)

  const handleBlur = () => {
    setValue(value.trim())
  }

  return (
    <Contain>
      {(props.type == InputTypeEnum.TEXT || props.type == InputTypeEnum.EMAIL) &&
        <div>
          {tooltipContent && <CustomTooltip title={tooltipTitle} content={tooltipContent} placement={"right"}/>}
          <FloatingLabel
            controlId={id}
            label={label}
            className={`mb-3 ${className}`}
          >
            {optional &&
              <span className={"optional"}><Badge type={"neutral"} text={optionalText} size={"small"}/></span>}
            <Form.Control type={type}
                          placeholder={placeholder}
                          value={value}
                          onChange={(event) => setValue(props.type === InputTypeEnum.EMAIL ? event.target.value.trim() : event.target.value)}
                          onBlur={handleBlur}
                          isInvalid={!!errorMsg || isInvalid}
                          required={required}
                          disabled={disabled}
                          minLength={minLength}
                          maxLength={maxLength}
            />
            {errorMsg && errorMsg.map((msg: string) => {
              return (
                <Form.Control.Feedback key={msg} type={"invalid"}>{parse(msg)}</Form.Control.Feedback>
              )
            })}
          </FloatingLabel>
        </div>
      }

      {props.type == InputTypeEnum.TEXTAREA &&
        <FloatingLabel
          controlId={id}
          label={label}
          className={`mb-3 ${className}`}
        >
          {optional && <span className={"optional"}><Badge type={"neutral"} text={optionalText} size={"small"}/></span>}
          <Form.Control type={type}
                        as="textarea"
                        style={{height: '100px'}}
                        placeholder={placeholder}
                        value={value}
                        onChange={(event) => setValue(event.target.value)}
                        onBlur={handleBlur}
                        isInvalid={!!errorMsg || isInvalid}
                        required={required}
                        disabled={disabled}
                        maxLength={maxLength}
                        minLength={minLength}
          />
          {errorMsg && errorMsg.map((msg: string) => {
            return (
              <Form.Control.Feedback key={msg} type={"invalid"}>{parse(msg)}</Form.Control.Feedback>
            )
          })}
        </FloatingLabel>
      }

      {props.type == InputTypeEnum.PASSWORD &&
        <FloatingLabel
          controlId={id}
          label={label}
          className={`mb-3 ${className}`}
        >
          {optional && <span className={"optional"}><Badge type={"neutral"} text={optionalText} size={"small"}/></span>}
          <Form.Control type={showPassword ? "text" : "password"}
                        placeholder={placeholder}
                        value={value}
                        onChange={(event) => setValue(event.target.value)}
                        onBlur={handleBlur}
                        isInvalid={!!errorMsg || isInvalid}
                        required={required}
                        disabled={disabled}
                        maxLength={maxLength}
          />

          <span className="show-password" onClick={() => setShowPassword(!showPassword)}>
            <Icon color="secondaryFont" variant={"small"}>{showPassword ? "eye-line" : "eye-off-line"}</Icon>
          </span>
          {errorMsg && errorMsg.map((msg: string) => {
            return (
              <Form.Control.Feedback key={msg} type={"invalid"}>{msg}</Form.Control.Feedback>
            )
          })}
        </FloatingLabel>
      }
    </Contain>
  )
}

export default CustomInput